import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Wallet, AlertTriangle, Info, Star, PiggyBank } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import PetanqueLoader from '../components/PetanqueLoader';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { api } from '../api/sangly';
import { moneyExact, fmtDate, timeAgo, CURRENCIES } from '../lib/format';
import { cn } from '../lib/utils';

const NAMES = Object.fromEntries(CURRENCIES);
const RESERVE_KEY = 'sangly.paypal.reserve';
const DEFAULT_RESERVE = 5000;

// A rate with as many decimals as it needs to be readable: 1.0610 for CHF→EUR,
// 0.005581 for JPY→EUR - fixed 4 decimals would print JPY as 0.0056.
const fmtRate = (r) => {
  const n = Number(r);
  if (!n) return '-';
  const digits = n >= 1 ? 4 : Math.min(8, Math.max(4, -Math.floor(Math.log10(n)) + 3));
  return n.toLocaleString('nl-BE', { minimumFractionDigits: 4, maximumFractionDigits: digits });
};
// "14:05" for a timestamp of today, "8 Sep, 14:05" otherwise (local time)
const fmtTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const sameDay = d.toDateString() === new Date().toDateString();
  return d.toLocaleString('nl-BE', { ...(sameDay ? {} : { day: 'numeric', month: 'short' }), hour: '2-digit', minute: '2-digit' });
};
// exact money, but "-" for a figure PayPal / the ECB did not give us
const amt = (v, ccy) => (v === null || v === undefined ? <span className="text-muted-foreground">-</span> : moneyExact(v, ccy));

const readReserve = () => {
  try {
    const v = Number(localStorage.getItem(RESERVE_KEY));
    return Number.isFinite(v) && v >= 0 && localStorage.getItem(RESERVE_KEY) !== null ? v : DEFAULT_RESERVE;
  } catch { return DEFAULT_RESERVE; }
};

export default function PayPal() {
  // the reserve applied to the table, and the text in the box (debounced into it)
  const [reserve, setReserve] = useState(readReserve);
  const [reserveText, setReserveText] = useState(() => String(readReserve()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [, setTick] = useState(0);          // re-render every 30 s so "x min ago" stays true
  useEffect(() => { const t = setInterval(() => setTick((n) => n + 1), 30000); return () => clearInterval(t); }, []);

  const load = useCallback(async (check = false) => {
    setErr('');
    if (check) setBusy(true); else setLoading(true);
    try { setData(await api.paypal.balances(reserve, check)); }
    catch (e) { setErr(e?.response?.data?.detail || 'Could not load the PayPal balances.'); }
    finally { setLoading(false); setBusy(false); }
  }, [reserve]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => {
      const v = Number(String(reserveText).replace(',', '.'));
      if (Number.isFinite(v) && v >= 0 && v !== reserve) {
        setReserve(v);
        try { localStorage.setItem(RESERVE_KEY, String(v)); } catch { /* private mode */ }
      }
    }, 500);
    return () => clearTimeout(t);
  }, [reserveText, reserve]);

  const rows = data?.rows || [];
  const hasRates = !!data && !data.rates_error;

  return (
    <AppLayout title="PayPal"
               description="Balances in every currency, their value in EUR, and what each can pay out while keeping a reserve"
               actions={(
                 <button onClick={() => load(true)} disabled={busy || loading}
                   title="Ask PayPal for the balances again, right now (logs in afresh). PayPal dates the figures itself."
                   className="inline-flex h-9 items-center gap-1.5 rounded-md border border-primary/40 bg-card px-3 text-[13px] font-medium hover:bg-muted disabled:opacity-50">
                   <RefreshCw className={cn('h-4 w-4 text-primary', busy && 'animate-spin')} /> {busy ? 'Refreshing…' : 'Refresh balances'}
                 </button>
               )}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-[12px] text-muted-foreground" title="Kept in every currency: the withdrawable amount is the balance minus this reserve, expressed in that currency">
          <PiggyBank className="h-3.5 w-3.5" /> Reserve per currency
          <div className="relative">
            <Input type="text" inputMode="decimal" value={reserveText} onChange={(e) => setReserveText(e.target.value)}
                   className="h-9 w-28 pr-7 text-right tnum text-foreground" />
            <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[12px] text-muted-foreground">€</span>
          </div>
        </label>
        <div className="ml-auto flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
          {data && (
            <Badge variant={data.environment === 'live' ? 'success' : 'warning'}>
              {data.environment === 'live' ? 'Live' : 'Sandbox'}{data.host ? ` · ${data.host}` : ''}
            </Badge>
          )}
          {data?.account_id && <span>account <span className="font-mono">{data.account_id}</span></span>}
          {data?.fetched_at && (
            <span title={`Our last call to PayPal: ${data.fetched_at}`}>· fetched from PayPal {timeAgo(data.fetched_at)}</span>
          )}
          {(data?.last_refresh_time || data?.as_of_time) && (
            <span title={`PayPal's own timestamp on these figures (as_of_time ${data.as_of_time || '-'}, last_refresh_time ${data.last_refresh_time || '-'}). PayPal refreshes its balance report on its side; asking again returns the same figures until it does.`}>
              · PayPal figures as of {fmtTime(data.last_refresh_time || data.as_of_time)}
            </span>
          )}
          {hasRates && data.rates_date && <span>· ECB rates of {fmtDate(data.rates_date)}</span>}
          {data?.fallback_used && data.fallback_date && (
            <span title="Used only for the currencies the ECB does not quote">· {data.fallback_source} rates of {fmtDate(data.fallback_date)}</span>
          )}
        </div>
      </div>

      {err && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-500/10 p-3 text-[13px] text-rose-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{err}
        </div>
      )}
      {data && !data.enabled && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-500/10 p-3 text-[13px] text-amber-800">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-medium">PayPal is not connected yet.</div>
            Set <span className="font-mono">PAYPAL_CLIENT_ID</span> and <span className="font-mono">PAYPAL_CLIENT_SECRET</span> in
            the backend environment (Coolify → Environment Variables) with the credentials of a REST app that has the
            “Transaction Search” feature - see the README. Nothing is stored in the repo.
          </div>
        </div>
      )}
      {data?.enabled && data.error && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-500/10 p-3 text-[13px] text-rose-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div><span className="font-medium">PayPal did not answer.</span> {data.error}</div>
        </div>
      )}
      {data?.enabled && !data.error && data.rates_error && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-500/10 p-3 text-[13px] text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div><span className="font-medium">No conversion.</span> {data.rates_error} The balances are shown as PayPal reports them.</div>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Currency</TableHead>
              <TableHead className="text-right">Total balance</TableHead>
              <TableHead className="text-right">Value in EUR</TableHead>
              <TableHead className="text-right">Available for withdraw</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={4} className="h-40 text-center">
                <PetanqueLoader size={34} roll className="mx-auto" />
              </TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={4} className="h-40 text-center text-muted-foreground">
                {data?.enabled && !data.error ? 'PayPal reports no balance in any currency.' : 'No balances to show.'}
              </TableCell></TableRow>
            )}
            {!loading && rows.map((r) => {
              const w = r.withdrawable === null || r.withdrawable === undefined ? null : Number(r.withdrawable);
              return (
                <TableRow key={r.currency}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="grid h-6 w-6 place-items-center rounded-md bg-primary/12"><Wallet className="h-3.5 w-3.5 text-primary" /></span>
                      <div>
                        <div className="font-medium leading-tight">{r.currency}{r.primary && <Star className="ml-1 inline h-3 w-3 fill-amber-400 text-amber-400" title="Primary currency of the account" />}</div>
                        <div className="text-[11px] text-muted-foreground">{NAMES[r.currency] || ''}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-semibold tnum">{amt(r.total, r.currency)}</TableCell>
                  <TableCell className="text-right tnum">
                    {r.rate_missing ? (
                      <Badge variant="warning" title="Neither the ECB nor the complementary source quotes this currency">no rate</Badge>
                    ) : (
                      <div title={r.currency === 'EUR' ? undefined : `1 ${r.currency} = ${fmtRate(r.rate)} EUR · 1 EUR = ${fmtRate(r.inverse_rate)} ${r.currency} · source: ${r.rate_source}`}>
                        <div className="font-semibold">{amt(r.converted, 'EUR')}</div>
                        {r.currency !== 'EUR' && (
                          <div className="text-[11px] text-muted-foreground">
                            @ {fmtRate(r.rate)}
                            {r.rate_source && r.rate_source !== 'ECB' && (
                              <span className="ml-1 rounded border border-sky-200 bg-sky-500/10 px-1 text-[10px] text-sky-700" title="The ECB does not quote this currency; rate from the complementary source">{r.rate_source}</span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right tnum">
                    {w === null ? (
                      <span className="text-muted-foreground">-</span>
                    ) : w > 0 ? (
                      <div title={`${moneyExact(r.total, r.currency)} minus a reserve of ${moneyExact(r.reserve_in_currency, r.currency)} (= ${moneyExact(data.reserve, 'EUR')})`}>
                        <div className="font-semibold text-emerald-700">{moneyExact(w, r.currency)}</div>
                        <div className="text-[11px] text-muted-foreground">keeps {moneyExact(r.reserve_in_currency, r.currency)}</div>
                      </div>
                    ) : (
                      <div title={`The balance is below the reserve of ${moneyExact(r.reserve_in_currency, r.currency)} (= ${moneyExact(data.reserve, 'EUR')})`}>
                        <div className="text-muted-foreground">{moneyExact(0, r.currency)}</div>
                        <div className="text-[11px] text-amber-700">{moneyExact(-w, r.currency)} below reserve</div>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && rows.length > 0 && data?.total_converted !== null && data?.total_converted !== undefined && (
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableCell colSpan={2} className="font-medium">
                  Total
                  {data.unconverted?.length > 0 && (
                    <span className="ml-2 text-[11px] font-normal text-amber-700">
                      excluding {data.unconverted.join(', ')} (no rate)
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right text-base font-semibold tnum">{moneyExact(data.total_converted, 'EUR')}</TableCell>
                <TableCell className="text-right tnum" title="The withdrawable amounts converted back into EUR at the same rates">
                  <span className="font-semibold text-emerald-700">≈ {moneyExact(data.total_withdrawable_converted, 'EUR')}</span>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {data?.enabled && !data.error && data.fallback_error && (
        <div className="mt-3 flex items-start gap-2 text-[12px] text-amber-700">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>The complementary rate source did not answer ({data.fallback_error}); currencies the ECB does not quote are shown without a rate.</span>
        </div>
      )}
      <div className="mt-3 text-[12px] text-muted-foreground">
        Value in EUR uses the European Central Bank&apos;s daily euro reference rates (published once a business day, ~16:00 CET);
        currencies the ECB does not quote use the daily rates of{' '}
        <a href="https://www.exchangerate-api.com" target="_blank" rel="noreferrer" className="underline hover:no-underline">Exchange Rate API</a>
        {' '}and are marked. PayPal publishes no consultable rate: it applies its own rate and fee at the moment of a conversion.
        {' '}Available for withdraw = total balance minus {moneyExact(data?.reserve ?? reserve, 'EUR')} expressed in that currency, so every
        currency keeps that reserve.
        {' '}<Star className="inline h-3 w-3 fill-amber-400 text-amber-400" /> = primary currency of the PayPal account.
      </div>
    </AppLayout>
  );
}
